<script setup>
import { computed, inject } from 'vue';
import { styleVars } from './shared.js';
import { forecastEmoji, forecastFor, forecastMode, forecastSlots } from '../forecastSlots.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const editing = inject('tiqEditorCanvas', false);

const data = computed(() => forecastFor(props.site_data, props.config.network_id));
const mode = computed(() => forecastMode(props.config.mode));
// One definition of the strip, shared with the forecast inside Trail
// conditions (forecastSlots.js).
const slots = computed(() => forecastSlots(data.value, mode.value, props.config.daily_count));
const hasData = computed(() => (mode.value === 'daily' ? slots.value.length > 0 : !!data.value?.current));

function temp(temp_c) {
    if (temp_c == null) return '—';
    if (data.value.unit_system === 'imperial') return `${Math.round((temp_c * 9 / 5) + 32)}°`;
    return `${Math.round(temp_c)}°`;
}

const emoji = forecastEmoji;
// White is the design's joined panel; transparent is the same strip
// straight on the block's background.
const transparent = computed(() => props.config.panel === 'transparent');
</script>

<template>
    <section v-if="hasData || editing" :id="config.anchor_id || null" class="th-block th-sec th-sec--tight th-fc" :style="styleVars(config)">
        <div class="th-wrap">
            <div v-if="config.heading || config.subtitle" class="th-fc__head">
                <h2 v-if="config.heading" class="th-h2 th-h2--sm">{{ config.heading }}</h2>
                <p v-if="config.subtitle" class="th-small">{{ config.subtitle }}</p>
            </div>

            <div v-if="hasData" class="th-fc__strip" :class="transparent ? 'th-fc__strip--bare' : 'th-panel'">
                <div v-for="(s, i) in slots" :key="i" class="th-fc__slot">
                    <div class="th-fc__when">{{ s.when }}</div>
                    <div class="th-fc__temp">
                        <span class="th-fc__icon" aria-hidden="true">{{ emoji(s.code) }}</span>
                        <template v-if="mode === 'daily'">
                            {{ temp(s.high_c) }}<span class="th-fc__low">{{ temp(s.low_c) }}</span>
                        </template>
                        <template v-else>{{ temp(s.temp_c) }}</template>
                    </div>
                    <div v-if="s.summary" class="th-fc__sky">{{ s.summary }}</div>
                </div>
            </div>

            <!-- Only inside the editor. On the live site the block
                 hides itself rather than printing an excuse, but an
                 invisible block on the canvas reads as a broken one. -->
            <p v-else class="th-small th-fc__pending">
                No forecast for this organization yet. This block shows nothing on the live site until the weather data lands.
            </p>
        </div>
    </section>
</template>

<style scoped>
.th-fc__head { margin-bottom: 16px; display: flex; flex-direction: column; gap: 6px; }
/* One joined strip, readings divided by rules: on a white panel by
   default, or straight on the block's background. */
.th-fc__strip {
    display: flex;
    flex-wrap: wrap;
    overflow: hidden;
}
.th-fc__slot {
    padding: 16px 18px;
    /* Every reading as wide as the row allows: a short last row (five
       days on a phone that fits three) STRETCHES rather than leaving a
       hole, and nothing is narrower than a reading needs, so a long
       high/low cannot run past the panel's edge. */
    flex: 1 1 128px;
    /* Rules drawn as shadows on the right and below; the strip clips
       the ones that land on its outer edge, wherever the rows break. */
    box-shadow: 1px 0 0 var(--th-line-soft), 0 1px 0 var(--th-line-soft);
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}
.th-fc__strip--bare .th-fc__slot:first-child { padding-left: 0; }
.th-fc__when {
    font: 500 11px/1 var(--th-mono);
    letter-spacing: .1em;
    text-transform: uppercase;
    color: var(--th-meta);
}
.th-fc__temp { font: 700 22px/1.1 var(--th-display); display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 6px; }
.th-fc__icon { font-size: 18px; line-height: 1; }
.th-fc__low { color: var(--th-faint); font-weight: 500; font-size: 16px; }
.th-fc__sky { font-size: 14px; color: var(--text-sub, var(--th-muted)); }
.th-fc__pending { font-style: italic; }
@media (max-width: 480px) {
    .th-fc__slot { padding: 14px 16px; }
}
</style>
